import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Box, Snackbar, Alert, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { getThemeStyles } from '../../themes/themeConfig';
import { useTranslation } from 'react-i18next';

// Importar hooks personalizados
import { useChat } from './hooks/useChat';
import { useMessageHandling } from './hooks/useMessageHandling';
import { useChatFilters } from './hooks/useChatFilters';

// Importar componentes
import ChatHeader from './components/ChatHeader';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
// Importar el nuevo ChatModal en lugar del sidebar
import ChatModal from '../modal/ChatModal';
import MobileChatModal from '../modal/MobileChatModal';
import ProjectChatView from '../projects/ProjectChatView';
import PromptCards from '../input/PromptCards';
import IntercomButton from '../../common/IntercomButton';
// Import Intercom service
import { showIntercom, trackIntercomEvent } from '../../common/services/IntercomService';
import { getProjects } from '../../AGTHall/services/AGTHallService';

import {
  getUserPersonalityPreference,
  setUserPersonalityPreference
} from '../../AGTHall/services/AGTHallService';

// Container principal
const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  '*::-webkit-scrollbar': {
    width: '8px !important',
    height: '8px !important',
  },
  '*::-webkit-scrollbar-track': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px !important',
  },
  '*::-webkit-scrollbar-thumb': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px !important',
    border: 'none !important',
    ':hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)',
    },
  },
  '*::-webkit-scrollbar-corner': {
    background: 'transparent !important',
  },
  ...getThemeStyles('ChatContainer', theme.palette.mode),
}));

// Area principal del chat - Modificada para no depender del sidebar
const ChatArea = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== '$withHeader'
})(({ theme, $withHeader }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  transition: 'all 0.3s ease',
  paddingTop: $withHeader ? '48px' : '0',
  opacity: 1,
  position: 'relative',
  left: 'auto',
  '*::-webkit-scrollbar': {
    width: '8px !important',
    height: '8px !important',
  },
  '*::-webkit-scrollbar-track': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px !important',
  },
  '*::-webkit-scrollbar-thumb': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px !important',
    border: 'none !important',
    ':hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)',
    },
  },
  '*::-webkit-scrollbar-corner': {
    background: 'transparent !important',
  },
  ...getThemeStyles('ChatArea', theme.palette.mode),
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '14px', // Fixed height
  minHeight: '14px', // Fixed minimum height
  maxHeight: '14px', // Fixed maximum height
  padding: 0, // Remove padding
  backgroundColor: 'transparent',
  display: 'flex',
  justifyContent: 'flex-end', // Already set to flex-end which is good
  alignItems: 'center',
  position: 'relative', // Changed from sticky to relative
  bottom: 0,
  zIndex: 1000,
  paddingRight: '6px', // Keep right padding for desktop
  [theme.breakpoints.down('sm')]: {
    paddingRight: '2px', // Increase right padding on mobile
  },
  ...getThemeStyles('FooterContainer', theme.palette.mode),
}));

/**
 * ChatViewNest - Componente principal de la vista de chat
 * 
 * Integra los hooks y componentes refactorizados para crear una experiencia
 * de chat completa con modal, mensajes y entrada de mensajes.
 */
const ChatViewNest = React.memo(({
  hideSidebar = false,
  hideFooter = false,
  user,
  isAuthorized,
  themeMode,
  onSendMessage,
  isLimited,
  initialView,
  messages: initialMessages,
  isLoading: initialIsLoading,
  onNavigate,
  mostRecentChatId,
  onClearMostRecentChatId, // Nueva prop para limpiar el ID más reciente
  onOpenAGTPromo,
  userName,
  projectContext,
  initialChatMessage,
  onClearInitialMessage,
  onUserPreferencesChanged, // Callback cuando el asistente actualiza preferencias
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Refs
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const projectsSectionRef = useRef(null);
  const userScrollingRef = useRef(false);
  const userScrollTimer = useRef(null); // New ref for tracking scroll inactivity
  const messageHookRef = useRef(null);
  
  // Estado para modal en vez de sidebar
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Estado para snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Add state to track Ultra alert visibility on mobile
  const [isUltraAlertVisible, setIsUltraAlertVisible] = useState(false);
  
  // Utilizar hooks personalizados
  const chatHook = useChat(user, initialMessages, initialIsLoading);
  
  const {
    chats,
    currentChatId,
    messages,
    visibleMessages,
    chatContext,
    chatTitle,
    isEditingTitle,
    editedTitle,
    isNewChatView,
    error,
    setError,
    retryAction,
    loadingStates,
    projectIdForBackend,
    selectedCategory,
    selectedProject,
    createNewChatHandler: originalCreateNewChatHandler, // Renombrado para usar nuestra versión mejorada
    handleTitleEdit,
    handleTitleSave,
    handleDeleteChat,
    updateChatCategory,
    selectChat,
    handleSelectProject,
    handleProjectChatSelect,
    persistentChatState,
    setPersistentChatState,
    setSelectedProject,
    setChatTitle,
    setCurrentChatId,
    setEditedTitle,
    saveCurrentChatId,
    updateChatsOrder,
    setCachedChat,
    setChatContext,
    setChats,
    setHasNextPage,
    setLastEvaluatedKey,
    setIsNewChatView,
    setMessages,
    setVisibleMessages,
  } = chatHook;
  
  // Handler for Ultra alert state changes
  const handleUltraAlertStateChange = useCallback((isVisible) => {
    setIsUltraAlertVisible(isVisible);
  }, []);
  
  // Versión mejorada de createNewChatHandler que limpia el ID más reciente
  const createNewChatHandler = useCallback(() => {
    // Primero llamamos a la implementación original
    originalCreateNewChatHandler();

    // Track event in Intercom
    trackIntercomEvent('created_chat');

    // Luego limpiamos el ID más reciente en el componente padre si existe la función
    if (typeof onClearMostRecentChatId === 'function') {
      onClearMostRecentChatId();
    }
  }, [originalCreateNewChatHandler, onClearMostRecentChatId]);
  
  
  // Función para scroll a la parte inferior - MEJORADA
  const scrollToBottom = useCallback(
    (options = { force: false }) => {
      if (!messagesEndRef.current) return;

      // Reset user scrolling state if force scroll
      if (options.force) {
        userScrollingRef.current = false;
      }

      // Only scroll if forced OR user is not manually scrolling
      if (options.force || !userScrollingRef.current) {
        // Use requestAnimationFrame for smoother scrolling
        requestAnimationFrame(() => {
          try {
            // Try multiple methods for maximum compatibility

            // Method 1: Direct parent scrolling (most reliable)
            const container = messagesEndRef.current.parentElement;
            if (container) {
              container.scrollTo({
                top: container.scrollHeight,
                behavior: options.force ? 'auto' : 'smooth'
              });
            }

            // Method 2: scrollIntoView (works in most browsers)
            messagesEndRef.current.scrollIntoView({
              behavior: options.force ? 'auto' : 'smooth',
              block: 'end',
            });

            // Method 3: Force scroll as last resort
            if (options.force) {
              setTimeout(() => {
                if (container) {
                  container.scrollTop = container.scrollHeight;
                }
              }, 50);
            }
          } catch (error) {
            console.error('Error scrolling to bottom:', error);
          }
        });
      }
    },
    []
  );

  // NUEVA función: Scroll para posicionar el último mensaje en la parte SUPERIOR de la vista
  // Esto crea el efecto "Claude.ai" donde cada nuevo mensaje aparece en una vista limpia
  const scrollToNewMessage = useCallback(() => {
    requestAnimationFrame(() => {
      try {
        // Encontrar el contenedor de mensajes
        const messagesContainer = document.querySelector('[role="main"] > div > div');
        if (!messagesContainer) return;

        const allMessages = messagesContainer.querySelectorAll('[class*="MessageContainer"]');
        if (allMessages.length === 0) return;

        // Si solo hay un mensaje, no hay nada que ocultar
        if (allMessages.length === 1) return;

        // Calcular la altura total de TODOS los mensajes anteriores (excepto el último)
        let totalHeightToHide = 0;
        for (let i = 0; i < allMessages.length - 1; i++) {
          const messageHeight = allMessages[i].offsetHeight;
          const messageStyle = window.getComputedStyle(allMessages[i]);
          const marginBottom = parseFloat(messageStyle.marginBottom) || 0;
          totalHeightToHide += messageHeight + marginBottom;
        }

        // Agregar un poco de padding extra para que el nuevo mensaje quede bien arriba
        const paddingTop = 20;

        // Hacer scroll para ocultar todos los mensajes anteriores
        messagesContainer.scrollTo({
          top: totalHeightToHide - paddingTop,
          behavior: 'smooth'
        });

        userScrollingRef.current = false;
      } catch (error) {
        console.error('Error scrolling to new message:', error);
      }
    });
  }, []);
  
  // Manejo del modelo de chat (AGT)
  const [selectedModel, setSelectedModel] = useState(() => {
    // Intentar cargar del localStorage primero
    const storedModel = localStorage.getItem('selectedModel');
    // Deprecated: Only AGT Nest is available now
    return storedModel || 'agt-nest-20250314';
  });
  
  // Personalidad del chat
  const [selectedPersonality, setSelectedPersonality] = useState(() => {
    // Intentar cargar del localStorage primero
    const storedPersonality = localStorage.getItem('selectedPersonality');
    // Si no hay valor guardado, usar 'normal'
    return storedPersonality || 'normal';
  });
  
  // Message handling hook
  const messageHook = useMessageHandling({
    user,
    chatContext,
    setChatContext,
    messages,
    setMessages,
    setVisibleMessages,
    currentChatId,
    setCurrentChatId,
    saveCurrentChatId,
    setIsNewChatView,
    setChats,
    setHasNextPage,
    setLastEvaluatedKey,
    updateChatsOrder,
    setChatTitle,
    projectIdForBackend,
    selectedCategory,
    selectedModel,
    selectedPersonality,
    userName,
    setCachedChat,
    projectContext,
    scrollToBottom,
    messagesEndRef, // Agregar messagesEndRef para auto-scroll inteligente
    onUserPreferencesChanged // Pasar callback de actualización de preferencias
  });
  
  messageHookRef.current = messageHook;
  
  const {
    isWaitingForResponse,
    isWaitingForServerResponse, // Nuevo estado extraído del hook
    isStreaming, // Nuevo estado para detectar streaming activo
    isLoadingVersion, // Estado para indicar si se está cargando una versión
    waitingStartTime,
    lastMessageId,
    lastImageRequestId,
    lastAssistantMessage,
    ultraThoughts,
    messageTypeInfo,
    activeTools, // Estado de tools en ejecución
    handleSendMessage,
    handleNewChatInput,
    handleUpdateMessage,
    stopStreaming, // Función para detener el streaming
    handleRetry,
    rateLimitInfo,
    userScrollingRef: messageUserScrollingRef,
    // Handlers de versionado
    handleEditMessage,
    handleResendMessage,
    handleSwitchVersion,
  } = messageHook;
  
  // Filters hook
  const filterHook = useChatFilters(
    user,
    chats,
    selectedCategory,
    setChats,
    setHasNextPage,
    setLastEvaluatedKey,
    setError,
    retryAction
  );
  
  const {
    searchTerm,
    setSearchTerm,
    isSearchFocused,
    setIsSearchFocused,
    selectedFilters,
    filteredChats,
    handleFilterToggle,
    clearAllFilters,
    filterCategories,
    loadMoreFilteredChats,
    isNextPageLoading,
    hasNextPage
  } = filterHook;
  
  // Sincronizar userScrollingRef entre hooks
  userScrollingRef.current = messageUserScrollingRef.current;
  
  // Mejorar detección de scroll del usuario
  useEffect(() => {
    const handleUserScroll = () => {
      userScrollingRef.current = true;
      
      // Reiniciar después de un período de inactividad
      clearTimeout(userScrollTimer.current);
      userScrollTimer.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, 5000); // 5 segundos de inactividad resetea el estado de scroll
    };
    
    const messagesContainer = document.querySelector('.MuiBox-root[role="main"] > div > div');
    if (messagesContainer) {
      messagesContainer.addEventListener('wheel', handleUserScroll, { passive: true });
      messagesContainer.addEventListener('touchmove', handleUserScroll, { passive: true });
    }
    
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('wheel', handleUserScroll);
        messagesContainer.removeEventListener('touchmove', handleUserScroll);
      }
      clearTimeout(userScrollTimer.current);
    };
  }, []);
  
  // Track errors in Intercom
  useEffect(() => {
    if (error) {
      trackIntercomEvent('encountered_error', {
        errorMessage: error,
        location: 'chat_view',
        chatId: currentChatId || 'new_chat'
      });
    }
  }, [error, currentChatId]);
  
  // Función para manejar envío de mensajes con scroll automático
  const handleSendMessageWithScroll = useCallback(
    (message) => {
      // NUEVO: Posicionar el mensaje en la parte SUPERIOR (estilo Claude.ai)
      // Esperar para que el mensaje se renderice completamente antes de hacer scroll
      setTimeout(() => {
        scrollToNewMessage();
      }, 300); // Aumentado a 300ms para asegurar que el DOM esté listo

      // Track event in Intercom
      trackIntercomEvent('sent_message', {
        messageType: message.type || 'text',
        chatId: currentChatId || 'new_chat'
      });

      // Llamar al handler original
      return handleSendMessage(message);
    },
    [handleSendMessage, scrollToNewMessage, currentChatId]
  );
  
  // Lo mismo para mensajes nuevos
  const handleNewChatInputWithScroll = useCallback(
    (message) => {
      // NUEVO: Posicionar el mensaje en la parte SUPERIOR (estilo Claude.ai)
      // Esperar para que el mensaje se renderice completamente antes de hacer scroll
      setTimeout(() => {
        scrollToNewMessage();
      }, 300); // Aumentado a 300ms para asegurar que el DOM esté listo

      // Track event in Intercom
      trackIntercomEvent('sent_first_message', {
        messageType: message.type || 'text'
      });

      // Limpiar el ID más reciente cuando se crea un nuevo chat
      if (typeof onClearMostRecentChatId === 'function') {
        onClearMostRecentChatId();
      }

      // Llamar al handler original
      return handleNewChatInput(message);
    },
    [handleNewChatInput, scrollToNewMessage, onClearMostRecentChatId]
  );
  
  // Open and close modal
  const openModal = useCallback(() => {
    setIsModalOpen(true);
    trackIntercomEvent('opened_chat_modal');
  }, []);
  
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  
  const handleModelChange = useCallback(
    (newModel) => {
      // Deprecated: Pro/Ultra/A1 model checks removed - only Nest available

      // Track model change in Intercom
      trackIntercomEvent('changed_model', {
        fromModel: selectedModel,
        toModel: newModel
      });

      // Guardar en estado y en localStorage
      setSelectedModel(newModel);
      localStorage.setItem('selectedModel', newModel);
    },
    [selectedModel]
  );

  const handlePersonalityChange = useCallback(
    async (newPersonality) => {
      // Verificamos si la personalidad seleccionada es solo para Pro
      const personalities = [
        { id: 'normal', proOnly: false },
        { id: 'humor', proOnly: false },
        { id: 'charismatic', proOnly: true },
        { id: 'stoic', proOnly: true }
      ];
   
      const isProOnly = personalities.find(p => p.id === newPersonality)?.proOnly;
   
      // Si es pro-only y el usuario no es Pro, mostrar promoción
      if (isProOnly && !user?.isPro) {
        onOpenAGTPromo();
        trackIntercomEvent('viewed_pro_personality_promotion', { requestedPersonality: newPersonality });
        return;
      }
   
      try {
        await setUserPersonalityPreference(user.uid, newPersonality);
        
        // Track personality change in Intercom
        trackIntercomEvent('changed_personality', { 
          fromPersonality: selectedPersonality, 
          toPersonality: newPersonality 
        });
        
        setSelectedPersonality(newPersonality);
        localStorage.setItem('selectedPersonality', newPersonality);
      } catch (error) {
        console.error('Error saving personality preference:', error);
        setSnackbarMessage('Error saving personality preference');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        
        trackIntercomEvent('personality_change_error', { 
          error: error.message,
          requestedPersonality: newPersonality
        });
      }
    },
    [user?.uid, user?.isPro, onOpenAGTPromo, selectedPersonality]
  );
  
  // Manejo de notificación del snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };
  
  // Seleccionar chat si hay un mostRecentChatId - MODIFICADO
  useEffect(() => {
    if (mostRecentChatId && mostRecentChatId !== currentChatId) {
      // Primero selecciona el chat
      selectChat(mostRecentChatId);
   
      // Luego limpia el ID más reciente después de un breve retraso
      // para asegurar que selectChat haya completado su ejecución
      setTimeout(() => {
        if (typeof onClearMostRecentChatId === 'function') {
          onClearMostRecentChatId();
        }
      }, 100);
    }
  }, [mostRecentChatId, selectChat, currentChatId, onClearMostRecentChatId]);
  
  // MODIFICADO: Efecto para manejar el modelo por default si es Pro o no
  useEffect(() => {
    const storedModel = localStorage.getItem('selectedModel');
    
    // Solo establecer el modelo por defecto si no hay selección previa del usuario
    // o si el usuario tenía un modelo Pro y ya no tiene acceso Pro
    if (!storedModel) {
      // Deprecated: Only AGT Nest available now
      const defaultModel = 'agt-nest-20250314';
      setSelectedModel(defaultModel);
      localStorage.setItem('selectedModel', defaultModel);
    } else if (storedModel !== 'agt-nest-20250314') {
      // Deprecated: If user had any other model selected, switch to Nest
      setSelectedModel('agt-nest-20250314');
      localStorage.setItem('selectedModel', 'agt-nest-20250314');
    } else {
      // Respetar la selección anterior del usuario
      setSelectedModel(storedModel);
    }
  }, [user?.isPro]);

  // MODIFICADO: Efecto para personalidad por default
  useEffect(() => {
    // Cargar preferencia de personalidad al iniciar
    const loadPersonalityPreference = async () => {
      try {
        if (user?.uid) {
          // Primero intentar cargar desde localStorage
          const storedPersonality = localStorage.getItem('selectedPersonality');
          
          if (storedPersonality) {
            // Verificar si es válida con el estado Pro actual
            if (!user?.isPro && storedPersonality !== 'normal') {
              setSelectedPersonality('normal');
              localStorage.setItem('selectedPersonality', 'normal');
            } else {
              setSelectedPersonality(storedPersonality);
            }
          } else {
            // Si no hay valor en localStorage, intentar cargar desde API
            const preference = await getUserPersonalityPreference(user.uid);
            // Verificar si es válida con el estado Pro actual
            if (!user?.isPro && preference !== 'normal') {
              setSelectedPersonality('normal');
              localStorage.setItem('selectedPersonality', 'normal');
            } else {
              setSelectedPersonality(preference);
              localStorage.setItem('selectedPersonality', preference);
            }
          }
        }
      } catch (error) {
        console.error('Error loading personality preference:', error);
        setSelectedPersonality('normal');
        localStorage.setItem('selectedPersonality', 'normal');
      }
    };
    
    loadPersonalityPreference();
  }, [user?.uid, user?.isPro]);

  // NUEVO: Efecto para recargar el mensaje hook cuando cambien los valores importantes
  useEffect(() => {
    // Este efecto no necesita hacer nada, solo está para garantizar que
    // messageHook tenga acceso a las versiones más recientes de selectedModel y selectedPersonality
  }, [selectedModel, selectedPersonality]);
  
  // Manejo de focus en el input
  useEffect(() => {
    if (!isWaitingForResponse) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isWaitingForResponse]);

  // Ref para rastrear si ya procesamos el mensaje inicial (evita duplicados)
  const processedInitialMessage = useRef(null);
  const isProcessingInitialMessage = useRef(false);

  // Procesar mensaje inicial desde Home - NO BLOQUEANTE
  useEffect(() => {
    // Solo procesar si:
    // 1. Hay un mensaje inicial
    // 2. No lo hemos procesado antes
    // 3. No estamos procesando actualmente
    if (
      initialChatMessage &&
      initialChatMessage !== processedInitialMessage.current &&
      !isProcessingInitialMessage.current
    ) {
      console.log('[ChatViewNest] Mensaje inicial detectado:', initialChatMessage);

      // Marcar como procesando INMEDIATAMENTE para evitar duplicados
      isProcessingInitialMessage.current = true;
      processedInitialMessage.current = initialChatMessage;

      // Limpiar el mensaje en el estado del padre INMEDIATAMENTE
      if (onClearInitialMessage) {
        onClearInitialMessage();
      }

      // CRÍTICO: Limpiar mensajes anteriores Y resetear estado del chat ANTES de procesar el nuevo mensaje
      // Esto asegura que cuando handleNewChatInput haga setMessages((prev) => [...prev, newMessage])
      // el prev esté vacío y no contenga mensajes del chat anterior
      flushSync(() => {
        console.log('[ChatViewNest] Limpiando mensajes anteriores antes de procesar...');
        setMessages([]);
        setVisibleMessages([]);
        // NUEVO: También resetear el currentChatId para forzar la creación de un nuevo chat
        setCurrentChatId(null);
        saveCurrentChatId(null);
        // NUEVO: Asegurar que estamos en modo de nuevo chat
        setIsNewChatView(true);
      });

      // IMPORTANTE: Ejecutar handleNewChatInput INMEDIATAMENTE (sin setTimeout)
      // para evitar race conditions con otros efectos que puedan cargar mensajes
      console.log('[ChatViewNest] Procesando mensaje inicial...');

      // NO usar await - dejar que corra en background
      handleNewChatInput(initialChatMessage)
        .then(() => {
          console.log('[ChatViewNest] Mensaje inicial procesado exitosamente');
          isProcessingInitialMessage.current = false;
        })
        .catch((error) => {
          console.error('[ChatViewNest] Error al procesar mensaje inicial:', error);
          isProcessingInitialMessage.current = false;
        });
    }
  }, [initialChatMessage, handleNewChatInput, onClearInitialMessage, setMessages, setVisibleMessages, setCurrentChatId, saveCurrentChatId, setIsNewChatView]);

  // Handler para prompt cards
  const handlePromptCardAction = useCallback((action) => {
    if (inputRef.current) {
      inputRef.current.handlePromptAction(action);
      trackIntercomEvent('used_prompt_card', { promptType: action?.type || action });
    }
  }, []);
  
  // Handler para crear proyectos
  const handleProjectClick = useCallback(() => {
    if (projectsSectionRef.current) {
      projectsSectionRef.current.handleNewProject();
      trackIntercomEvent('created_project');
    }
  }, []);

  // Obtener el chat actual
  const currentChatObj = chats.find((c) => c.id === currentChatId);

  const handleBackToProject = useCallback(async () => {
    if (selectedProject) {
      setSelectedProject((prev) => (prev ? { ...prev, currentView: 'main' } : prev));
      return;
    }

    const projectId = currentChatObj?.projectId;
    if (!projectId || !user?.uid) return;

    try {
      const projects = await getProjects(user.uid);
      const projectMatch = projects.find((project) => project.id === projectId);
      if (projectMatch) {
        setSelectedProject({ ...projectMatch, currentView: 'main' });
      }
    } catch (error) {
      console.error('Error loading project for back navigation:', error);
    }
  }, [selectedProject, currentChatObj?.projectId, user?.uid, setSelectedProject]);

  const handleCreateProjectChat = useCallback(async () => {
    const projectId = selectedProject?.id || currentChatObj?.projectId;
    const projectCategory = selectedProject?.category || currentChatObj?.category || 'personal';

    if (!user?.uid || !projectId) {
      return;
    }

    // Si no tenemos selectedProject activo, lo configuramos primero
    if (!selectedProject || selectedProject.id !== projectId) {
      try {
        const projects = await getProjects(user.uid);
        const projectMatch = projects.find((project) => project.id === projectId);
        if (projectMatch) {
          setSelectedProject({ ...projectMatch, currentView: 'chat' });
        } else {
          // Si no encontramos el proyecto completo, usamos la info del chat actual
          setSelectedProject({
            id: projectId,
            name: currentChatObj?.projectName || currentChatObj?.title || 'Proyecto',
            category: projectCategory,
            currentView: 'chat'
          });
        }
      } catch (error) {
        console.error('Error loading project:', error);
        // Continuar con la info que tenemos
        setSelectedProject({
          id: projectId,
          name: currentChatObj?.projectName || currentChatObj?.title || 'Proyecto',
          category: projectCategory,
          currentView: 'chat'
        });
      }
    }

    // MODIFICADO: No crea el chat inmediatamente, solo cambia la vista
    // El chat se creará cuando el usuario envíe el primer mensaje (evita chats vacíos)
    handleProjectChatSelect(null, false, 'chat'); // null = no hay chatId todavía
  }, [user?.uid, selectedProject, currentChatObj, handleProjectChatSelect, setSelectedProject]);

  const isProjectChatActive = Boolean(
    (selectedProject && selectedProject.currentView === 'chat') ||
    currentChatObj?.type === 'project' ||
    currentChatObj?.projectId
  );
  
  
  // Handle Intercom button click
  const handleIntercomClick = useCallback(() => {
    // Show Intercom messenger
    showIntercom();
    
    // Track event in Intercom
    trackIntercomEvent('opened_support', { 
      from: 'footer_button',
      chatId: currentChatId || 'new_chat',
      page: 'chat_view'
    });
  }, [currentChatId]);
  
  // Footer (solo se renderiza si hideFooter es false)
  const renderFooter = useCallback(() => {
    if (hideFooter) return null;
   
    return (
      <FooterContainer>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginTop: '-22px', // Move the icon up a bit
        }}>
          <IntercomButton onClick={handleIntercomClick} isMobile={isMobile} />
        </Box>
      </FooterContainer>
    );
  }, [hideFooter, handleIntercomClick, isMobile]);
  
  // Vista de prompt cards - Modificada para considerar la alerta de Ultra en móvil
  const renderPromptCards = () => {
    // If on mobile and Ultra alert is visible, don't show prompt cards
    if (isMobile && isUltraAlertVisible) {
      return null;
    }
    
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        padding="20px"
      >
        <PromptCards
          isPro={user?.isPro}
          userId={user?.uid}
          onSelectPrompt={handlePromptCardAction}
        />
      </Box>
    );
  };

  return (
    <ChatContainer>
      {/* Renderizamos el ChatModal o MobileChatModal según el dispositivo */}
      {!hideSidebar && (
        <>
          {isMobile ? (
            <MobileChatModal
              isOpen={isModalOpen}
              onClose={closeModal}
              user={user}
              createNewChatHandler={createNewChatHandler}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isSearchFocused={isSearchFocused}
              setIsSearchFocused={setIsSearchFocused}
              selectedCategory={selectedCategory}
              setSelectedCategory={chatHook.setSelectedCategory}
              selectedProject={selectedProject}
              currentChatId={currentChatId}
              filteredChats={filteredChats}
              selectChat={(chat) => {
                selectChat(chat);
                closeModal(); // Cerrar el modal después de seleccionar un chat
              }}
              handleMenuOpen={() => {}}
              hasNextPage={hasNextPage}
              isNextPageLoading={isNextPageLoading}
              loadChats={loadMoreFilteredChats}
              handleSelectProject={(project) => {
                handleSelectProject(project);
                if (project) closeModal(); // Cerrar el modal después de seleccionar un proyecto
              }}
              handleProjectChatSelect={(chat) => {
                handleProjectChatSelect(chat);
                closeModal(); // Cerrar el modal después de seleccionar un chat de proyecto
              }}
              persistentChatState={persistentChatState}
              setPersistentChatState={setPersistentChatState}
              projectsSectionRef={projectsSectionRef}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarOpen={setSnackbarOpen}
              handleDeleteChat={handleDeleteChat}
              updateChatCategory={updateChatCategory}
              filterCategories={filterCategories}
            />
          ) : (
            <ChatModal
              isOpen={isModalOpen}
              onClose={closeModal}
              user={user}
              createNewChatHandler={createNewChatHandler}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isSearchFocused={isSearchFocused}
              setIsSearchFocused={setIsSearchFocused}
              showMoreFilters={false}
              setShowMoreFilters={() => {}}
              selectedFilters={selectedFilters}
              handleFilterToggle={handleFilterToggle}
              clearAllFilters={clearAllFilters}
              filterCategories={filterCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={chatHook.setSelectedCategory}
              selectedProject={selectedProject}
              currentChatId={currentChatId}
              filteredChats={filteredChats}
              selectChat={(chat) => {
                selectChat(chat);
                closeModal(); // Cerrar el modal después de seleccionar un chat
              }}
              handleMenuOpen={() => {}}
              hasNextPage={hasNextPage}
              isNextPageLoading={isNextPageLoading}
              loadChats={loadMoreFilteredChats}
              handleSelectProject={(project) => {
                handleSelectProject(project);
                if (project) closeModal(); // Cerrar el modal después de seleccionar un proyecto
              }}
              handleProjectChatSelect={(chat) => {
                handleProjectChatSelect(chat);
                closeModal(); // Cerrar el modal después de seleccionar un chat de proyecto
              }}
              persistentChatState={persistentChatState}
              setPersistentChatState={setPersistentChatState}
              projectsSectionRef={projectsSectionRef}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarOpen={setSnackbarOpen}
              handleDeleteChat={handleDeleteChat}
              updateChatCategory={updateChatCategory}
            />
          )}
        </>
      )}

      <ChatArea
        $withHeader={!selectedProject || selectedProject.currentView === 'chat'}
        className={loadingStates.messages || loadingStates.chatInfo ? 'loading' : ''}
      >
        {/* MOSTRAMOS EL HEADER DEL CHAT SOLO SI NO hay un proyecto seleccionado */}
        {(!selectedProject || selectedProject.currentView === 'chat') && (
        <ChatHeader
          isMobile={isMobile}
          currentChatId={currentChatId}
          isNewChatView={isNewChatView}
          chatTitle={chatTitle}
          isEditingTitle={isEditingTitle}
          editedTitle={editedTitle}
          currentChatObj={currentChatObj}
          toggleSidebar={openModal}
          handleTitleEdit={handleTitleEdit}
          handleTitleSave={handleTitleSave}
          createNewChatHandler={createNewChatHandler}
          setEditedTitle={setEditedTitle}
          handleDeleteChat={handleDeleteChat}
          isSidebarOpen={false}
          hideSidebar={hideSidebar}
          user={user}
          isProjectChat={isProjectChatActive}
          onBackToProject={handleBackToProject}
          onCreateProjectChat={handleCreateProjectChat}
        />
      )}

        <Box display="flex" flexDirection="column" height="100%">
          <Box flexGrow={1} overflow="auto">
            {selectedProject && selectedProject.currentView !== 'chat' ? (
              <ProjectChatView
                project={selectedProject}
                onProjectChatSelect={handleProjectChatSelect}
                onProjectDeleted={() => setSelectedProject(null)}
                isWaitingForResponse={isWaitingForResponse}
                handleSendMessage={handleSendMessageWithScroll} // Usar versión con scroll
                inputRef={inputRef}
                rateLimitInfo={rateLimitInfo}
                onOpenAGTPromo={onOpenAGTPromo}
                selectedModel={selectedModel}
                onModelChange={handleModelChange}
                selectedPersonality={selectedPersonality}
                onPersonalityChange={handlePersonalityChange}
                isPro={user?.isPro}
                userId={user?.uid}
                error={error}
                onErrorClose={() => setError(null)}
                lastAssistantMessage={lastAssistantMessage}
                toggleSidebar={openModal} // Ahora abre el modal en lugar del sidebar
              />
            ) : isNewChatView || (!currentChatId && messages.length === 0) ? (
              renderPromptCards()
            ) : (
              <ChatMessages
                messages={visibleMessages}
                isWaitingForResponse={isWaitingForResponse}
                isWaitingForServerResponse={isWaitingForServerResponse} // Nuevo prop añadido
                isLoadingVersion={isLoadingVersion} // Estado de carga de versión
                waitingStartTime={waitingStartTime}
                lastMessageId={lastMessageId}
                handleUpdateMessage={handleUpdateMessage}
                lastImageRequestId={lastImageRequestId}
                messageTypeInfo={messageTypeInfo}
                ultraThoughts={ultraThoughts}
                activeTools={activeTools} // Tools en ejecución para mostrar en tiempo real
                error={error}
                handleRetry={handleRetry}
                user={user}
                scrollToBottom={scrollToBottom}
                userScrollingRef={userScrollingRef}
                messagesEndRef={messagesEndRef} // Pasar la referencia para el final de mensajes
                loadingStates={loadingStates} // Pasar el estado de carga
                onEditMessage={handleEditMessage}
                onResendMessage={handleResendMessage}
                onSwitchVersion={handleSwitchVersion}
              />
            )}
          </Box>

          {(!selectedProject || selectedProject.currentView === 'chat') && (
            <ChatInput
              ref={inputRef}
              handleSendMessage={currentChatId ? handleSendMessageWithScroll : handleNewChatInputWithScroll} // Usar versiones con scroll
              isWaitingForResponse={isWaitingForResponse}
              isStreaming={isStreaming}
              onStopStreaming={stopStreaming}
              rateLimitInfo={rateLimitInfo}
              onOpenAGTPromo={onOpenAGTPromo}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
              selectedPersonality={selectedPersonality}
              onPersonalityChange={handlePersonalityChange}
              isPro={user?.isPro}
              userId={user?.uid}
              chatId={currentChatId}
              error={error}
              onErrorClose={() => setError(null)}
              lastAssistantMessage={lastAssistantMessage}
              onPromptAction={handlePromptCardAction}
              onProjectClick={handleProjectClick}
              context={{ messages: visibleMessages }}  // Pass the messages array
              onCreateNewChat={createNewChatHandler}   // Pass the createNewChat function
              onUltraAlertStateChange={handleUltraAlertStateChange}  // Add this new prop
            />
          )}

          {renderFooter()}
        </Box>
      </ChatArea>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          action={
            error && retryAction ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleRetry}
                sx={{
                  borderRadius: '30px',
                  backgroundColor: 'action.selected',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {t('chat.messages.retry')}
              </Button>
            ) : null
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ChatContainer>
  );
});

// Add display name for debugging
ChatViewNest.displayName = 'ChatViewNest';

export default ChatViewNest;
